import { merge } from 'lodash';

type StyleParts = Record<string, any>;

const isPlainObject = (value: unknown): value is StyleParts =>
    null !== value && 'object' === typeof value && !Array.isArray(value);

// An sx array or theme callback cannot be deep-merged: lodash would replace the default part with it.
const extendPart = (base: unknown, override: unknown) => {
    if (isPlainObject(base) && isPlainObject(override)) return merge({}, base, override);
    const list = (value: unknown) => (Array.isArray(value) ? value : undefined === value ? [] : [value]);
    return [...list(base), ...list(override)];
};

/**
 * Applies one custom_styles section to a component's style parts. Both shapes are supported, also mixed:
 * - a map of parts keyed like the component's styles (`{ container: {...}, resizeHandler: {...} }`), the form
 *   custom apps were built on before MI-29973;
 * - flat sx for the root part (`{ outline: ... }`).
 * Keys naming a part go to that part; everything else goes to `rootPart`.
 */
export function mergeCustomStyles<T extends StyleParts>(defaults: T, custom: unknown, rootPart: keyof T & string): T {
    const styles: StyleParts = merge({}, defaults);
    if (undefined === custom || null === custom) return styles as T;

    if (!isPlainObject(custom)) {
        styles[rootPart] = extendPart(styles[rootPart], custom);
        return styles as T;
    }

    const flat: StyleParts = {};
    Object.entries(custom).forEach(([key, value]) => {
        if (Object.prototype.hasOwnProperty.call(defaults, key)) {
            styles[key] = extendPart(styles[key], value);
        } else {
            flat[key] = value;
        }
    });
    if (Object.keys(flat).length > 0) styles[rootPart] = extendPart(styles[rootPart], flat);

    return styles as T;
}
